/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, Suspense } from "react";
import { Switch, Redirect } from "react-router-dom";
import {
  Portal,
  useDisclosure,
  Stack,
  Box,
  useColorMode,
  Image,
} from "@chakra-ui/react";

import Sidebar from "components/Sidebar/Sidebar";
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import FixedPlugin from "components/FixedPlugin/FixedPlugin";
import Configurator from "components/Configurator/Configurator";
import MainPanel from "components/Layout/MainPanel";
import PanelContainer from "components/Layout/PanelContainer";
import PanelContent from "components/Layout/PanelContent";
import RoleProtectedRoute from "components/RoleProtectedRoute/RoleProtectedRoute";
import SplashScreen from "components/SplashScreen/SplashScreen";
import { useUser } from "contexts/UserContext";
import bgAdmin from "assets/img/admin-background.png";

// ✅ Yeni logo importları
import LogoColored from "assets/svg/logo-colored.svg";
import LogoWhite from "assets/svg/logo-white.svg";

import dashRoutes from "routes/dashRoutes";

export default function AdminLayout(props) {
  const [fixed, setFixed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true); // Sidebar varsayılan olarak açık
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, loading } = useUser();

  const ROUTES = Array.isArray(dashRoutes) ? dashRoutes : [];

  // Authentication kontrolü - User yoksa login'e yönlendir
  if (loading) {
    return <SplashScreen isLoading={true} message="Yükleniyor..." fullScreen={true} />;
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  const getActiveRoute = (routesArray) => {
    let activeRoute = "Default Brand Text";
    for (let i = 0; i < routesArray.length; i++) {
      const route = routesArray[i];
      if (route.collapse) {
        let collapseActiveRoute = getActiveRoute(route.views);
        if (collapseActiveRoute !== activeRoute) return collapseActiveRoute;
      } else if (route.category) {
        let categoryActiveRoute = getActiveRoute(route.views);
        if (categoryActiveRoute !== activeRoute) return categoryActiveRoute;
      } else if (window.location.href.indexOf(route.layout + route.path) !== -1) {
        return route.name || activeRoute;
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routesArray) => {
    let activeNavbar = false;
    for (let i = 0; i < routesArray.length; i++) {
      const route = routesArray[i];
      if (route.category) {
        let categoryActiveNavbar = getActiveNavbar(route.views);
        if (categoryActiveNavbar !== activeNavbar) return categoryActiveNavbar;
      } else if (window.location.href.indexOf(route.layout + route.path) !== -1) {
        if (route.secondaryNavbar) {
          return route.secondaryNavbar;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routesArray) =>
    routesArray.map((route, key) => {
      if (route.collapse) return getRoutes(route.views);
      if (route.category === "account") return getRoutes(route.views);
      if (
        route.layout === "/admin" &&
        route.component &&
        typeof route.component === "function"
      ) {
        return (
          <RoleProtectedRoute
            path={route.layout + route.path}
            component={route.component}
            key={key}
            exact
            allowedRoles={route.allowedRoles}
            adminOnly={route.adminOnly}
          />
        );
      }
      return null;
    });

  document.documentElement.dir = "ltr";

  // UserProvider artık index.js'de, burada tekrar eklemeye gerek yok
  // Ancak UserProvider zaten index.js'de olduğu için burada da olabilir (nested provider)
  // Ama daha temiz olması için buradan kaldırıyoruz
  return (
    <Box>
      <Box
        minH="40vh"
        w="100%"
        position="absolute"
        bgImage={colorMode === "light" ? bgAdmin : "none"}
        bg={colorMode === "light" ? bgAdmin : "navy.900"}
        bgSize="cover"
        top="0"
        pointerEvents="none"
        zIndex={0}
      />
      <Sidebar
        routes={ROUTES}
        logo={
          <Stack direction="row" spacing="12px" align="center" justify="center">
            <Image
              src={colorMode === "dark" ? LogoWhite : LogoColored}
              alt="ixpilot logo"
              h="40px"
              objectFit="contain"
            />
          </Stack>
        }
        display="none"
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        {...props}
      />
      <MainPanel
        w={{
          base: "100%",
          xl: sidebarOpen ? "calc(100% - 275px)" : "calc(100% - 95px)",
        }}
        transition="width 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      >
        <Portal>
          <AdminNavbar
            onOpen={onOpen}
            brandText={getActiveRoute(ROUTES)}
            secondary={getActiveNavbar(ROUTES)}
            fixed={fixed}
            sidebarOpen={sidebarOpen}
            onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
            {...props}
          />
        </Portal>

        {window.location.pathname !== "/admin/full-screen-maps" && (
          <PanelContent>
            <PanelContainer>
              <Suspense fallback={<SplashScreen isLoading={true} message="Sayfa yükleniyor..." fullScreen={true} />}>
                <Switch>
                  {getRoutes(ROUTES)}
                  <Redirect exact from="/admin" to="/admin/dashboard" />
                  {/* Olmayan route'lar için dashboard'a yönlendir (nginx production'da 404 handle ediyor) */}
                  <Redirect to="/admin/dashboard" />
                </Switch>
              </Suspense>
            </PanelContainer>
          </PanelContent>
        )}

        {/* Configurator ve FixedPlugin sadece development modunda göster */}
        {process.env.NODE_ENV === 'development' && (
          <>
            <Portal>
              <FixedPlugin
                secondary={getActiveNavbar(ROUTES)}
                fixed={fixed}
                onOpen={onOpen}
              />
            </Portal>

            <Configurator
              secondary={getActiveNavbar(ROUTES)}
              isOpen={isOpen}
              onClose={onClose}
              isChecked={fixed}
              onSwitch={(value) => setFixed(value)}
            />
          </>
        )}
      </MainPanel>
    </Box>
  );
}
